import React, { useState, useRef, useEffect } from 'react';
import { message as antMessage, App, Button, Flex, Badge } from 'antd';
import { CloudUploadOutlined, LinkOutlined, SendOutlined, EyeOutlined, FileImageOutlined, FileOutlined } from '@ant-design/icons';
import { Sender, Attachments, AttachmentsProps } from '@ant-design/x';
import type { GetProp, GetRef } from 'antd';
import { BASE_URL } from '../../config';
import { Message } from '../../types/chat';
import { OnlineUser } from '../../contexts/WebSocketProvider';
import ImageLightbox from './ImageLightbox';
import ReplyField from './ReplyField';
import EditField from './EditField';
import MentionsAutocomplete from './MentionsAutocomplete';
import { getMessageContent, getOriginalMessageText, parseRichMessage } from './utils';
import { useTranslation } from 'react-i18next';

interface MessageInputProps {
  roomId: string;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
  editingMessage?: Message | null;
  onCancelEdit?: () => void;
  webSocket?: WebSocket | null;
  onMentionUser?: (user: OnlineUser) => void;
}

interface UploadResult {
  success: boolean;
  filename: string;
  error?: unknown;
}

// Function to convert plain text with newlines to HTML
const convertTextToHtml = (text: string): string => {
  // First, convert URLs to anchor tags
  // This regex detects URLs with or without http/https prefix
  const urlRegex = /(\b(https?:\/\/)?[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
  
  const textWithLinks = text.replace(urlRegex, (url) => {
    // Add https:// prefix if the URL doesn't have a protocol
    const href = url.startsWith('http') ? url : `https://${url}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
  
  // Then, convert newlines to <br /> tags
  return textWithLinks.replace(/\n/g, '<br />');
};

const MessageInput: React.FC<MessageInputProps> = ({ 
  roomId, 
  replyingTo, 
  onCancelReply,
  editingMessage,
  onCancelEdit,
  webSocket,
  onMentionUser
}) => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  // State for the Sender component
  const [text, setText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // State for attachments
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentItems, setAttachmentItems] = useState<GetProp<AttachmentsProps, 'items'>>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // State for image lightbox
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  
  // Refs for components
  const senderRef = useRef<GetRef<typeof Sender>>(null);
  const attachmentsRef = useRef<GetRef<typeof Attachments>>(null);
  
  // Custom submit button state
  const [canSubmit, setCanSubmit] = useState(false);

  // Mentions autocomplete state
  const [mentionsVisible, setMentionsVisible] = useState(false);
  const [mentionsQuery, setMentionsQuery] = useState('');
  const [mentionsPosition, setMentionsPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  // Helper functions for localStorage operations
  const getDraftKey = (roomId: string) => `draft_message_${roomId}`;
  
  const saveDraft = (roomId: string, draftText: string) => {
    if (draftText.trim()) {
      localStorage.setItem(getDraftKey(roomId), draftText);
    } else {
      localStorage.removeItem(getDraftKey(roomId));
    }
  };
  
  const loadDraft = (roomId: string): string => {
    return localStorage.getItem(getDraftKey(roomId)) || '';
  };
  
  const deleteDraft = (roomId: string) => {
    localStorage.removeItem(getDraftKey(roomId));
  };

  // Focus input helper
  const focusInput = () => {
    // Small delay to ensure the component is ready
    setTimeout(() => {
      if (senderRef.current?.nativeElement) {
        const textarea = senderRef.current.nativeElement.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }
    }, 100);
  };

  // Mentions autocomplete helpers
  const hideMentions = () => {
    setMentionsVisible(false);
    setMentionsQuery('');
    setMentionStartIndex(-1);
  };

  const showMentions = (query: string, cursorPos: number) => {
    if (!senderRef.current?.nativeElement) return;
    
    const textarea = senderRef.current.nativeElement.querySelector('textarea');
    if (!textarea) return;

    // Calculate position for mentions dropdown
    const rect = textarea.getBoundingClientRect();
    const dropdownHeight = 200; // Max height of dropdown
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Decide whether to show dropdown above or below the input
    const showAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
    
    setMentionsPosition({
      top: showAbove 
        ? rect.top + window.scrollY - dropdownHeight - 5  // 5px gap above input
        : rect.bottom + window.scrollY + 5, // 5px gap below input
      left: rect.left + window.scrollX
    });
    
    setMentionsQuery(query);
    setMentionStartIndex(cursorPos - query.length - 1); // -1 for the @ symbol
    setMentionsVisible(true);
  };

  const handleMentionSelect = (user: OnlineUser) => {
    if (mentionStartIndex >= 0 && user.user_id) {
      // Extract username from user_id 
      // Handle both formats: @username:domain or username:domain
      const userPart = user.user_id.split(':')[0];
      const username = userPart.startsWith('@') ? userPart.substring(1) : userPart;
      const mentionText = `@${username}:`;
      
      // Replace the @ + query with the mention
      const beforeMention = text.substring(0, mentionStartIndex);
      const afterMention = text.substring(mentionStartIndex + mentionsQuery.length + 1);
      const newText = beforeMention + mentionText + ' ' + afterMention;
      
      setText(newText);
      hideMentions();
      
      // Callback to parent component if needed
      if (onMentionUser) {
        onMentionUser(user);
      }
      
      // Focus back to textarea
      setTimeout(() => {
        focusInput();
      }, 100);
    }
  };

  const handleTextChange = (value: string) => {
    setText(value);
    
    // Check for @ mentions
    const cursorPos = value.length; // Approximate cursor position
    const textBeforeCursor = value.substring(0, cursorPos);
    
    // Find the last @ symbol
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex >= 0) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
        // Extract text after @
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        
        // Check if there's no space in the text after @ (still typing the mention)
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          showMentions(textAfterAt, cursorPos);
          return;
        }
      }
    }
    
    // Hide mentions if no valid @ found
    hideMentions();
  };

  // Load draft when roomId changes
  useEffect(() => {
    if (roomId) {
      const savedDraft = loadDraft(roomId);
      setText(savedDraft);
    }
  }, [roomId]);

  // Save draft when text changes
  useEffect(() => {
    if (roomId) {
      saveDraft(roomId, text);
    }
  }, [text, roomId]);

  // Listen for storage changes from other tabs for synchronization
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === getDraftKey(roomId)) {
        const newValue = e.newValue || '';
        // Only update if the current text is different to avoid infinite loops
        if (newValue !== text) {
          setText(newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [roomId, text]);
  
  // Check if submission is allowed (has text or files)
  useEffect(() => {
    setCanSubmit(text.trim().length > 0 || attachmentItems.length > 0);
  }, [text, attachmentItems]);

  // Эффект для установки текста при редактировании
  useEffect(() => {
    if (editingMessage) {
      const originalText = getOriginalMessageText(editingMessage);
      setText(originalText);
    }
  }, [editingMessage]);

  // Function to send message text
  const sendMessage = async () => {
    if (!text.trim() || !roomId || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text,
          roomsID: [roomId]
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Clear the input field after successful send
      setText('');
      // Возвращаем фокус в поле ввода
      focusInput();
      
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Function to send reply message
  const sendReplyMessage = async () => {
    if (!text.trim() || !roomId || !replyingTo || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        // Отправляем через WebSocket
        const replyMessage = {
          type: 'reply_message',
          roomId,
          eventId: replyingTo.event_id,
          replyText: text.trim()
        };
        
        webSocket.send(JSON.stringify(replyMessage));
      } else {
        // Fallback на HTTP
        const token = localStorage.getItem('token');
        const response = await fetch(`${BASE_URL}/reply-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            roomId,
            eventId: replyingTo.event_id,
            replyText: text.trim()
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to send reply');
        }
      }
      
      // Clear the input field and cancel reply after successful send
      setText('');
      if (onCancelReply) {
        onCancelReply();
      }
      // Возвращаем фокус в поле ввода
      focusInput();
      
    } catch (error) {
      console.error('Error sending reply:', error);
      message.error('Failed to send reply. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Function to send edited message
  const sendEditMessage = async () => {
    if (!text.trim() || !roomId || !editingMessage || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        // Отправляем через WebSocket
        const editMessage = {
          type: 'edit_message',
          roomId,
          eventId: editingMessage.event_id,
          newText: text.trim()
        };
        
        webSocket.send(JSON.stringify(editMessage));
      } else {
        // Fallback на HTTP
        const token = localStorage.getItem('token');
        const response = await fetch(`${BASE_URL}/edit-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            roomId,
            eventId: editingMessage.event_id,
            newText: text.trim()
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to edit message');
        }
      }
      
      // Clear the input field and cancel edit after successful send
      setText('');
      if (onCancelEdit) {
        onCancelEdit();
      }
      // Возвращаем фокус в поле ввода
      focusInput();
      
    } catch (error) {
      console.error('Error editing message:', error);
      message.error('Failed to edit message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Function to upload one file
  const uploadFile = async (file: File): Promise<UploadResult> => {
    if (!roomId) return { success: false, filename: file.name, error: 'No room ID' };
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('roomId', roomId);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      return { success: true, filename: file.name };
      
    } catch (error) {
      console.error('Error uploading file:', error);
      return { success: false, filename: file.name, error };
    }
  };

  // Function to handle file upload from Attachments component
  const handleFileBeforeUpload = (file: File) => {
    // Keep the file in the component state but don't upload it yet
    return false;
  };

  // Handle submission (send files and message)
  const handleSubmit = async () => {
    // Если редактируем сообщение, не разрешаем загрузку файлов
    if (editingMessage && attachmentItems.length > 0) {
      message.warning('Нельзя добавлять файлы при редактировании сообщения');
      return;
    }

    // Only proceed if there are files to upload or text to send
    if (attachmentItems.length === 0 && !text.trim()) {
      return;
    }

    setUploadingFile(true);
    
    try {
      // First upload all files (only if not editing)
      if (attachmentItems.length > 0 && !editingMessage) {
        const results: UploadResult[] = [];
        
        for (const item of attachmentItems) {
          if (item.originFileObj) {
            const result = await uploadFile(item.originFileObj);
            results.push(result);
          }
        }
        
        const successes = results.filter(r => r.success === true).length;
        const failures = results.filter(r => r.success === false).length;
        
        if (successes > 0) {
          message.success(`${successes} file(s) uploaded successfully`);
        }
        
        if (failures > 0) {
          message.error(`${failures} file(s) failed to upload`);
        }
      }
      
      // Then send the appropriate message based on state
      if (text.trim()) {
        if (editingMessage) {
          await sendEditMessage();
        } else if (replyingTo) {
          await sendReplyMessage();
        } else {
          await sendMessage();
        }
      }
      
      // Clear everything (but only attachments if not editing)
      if (!editingMessage) {
        setAttachmentItems([]);
      }
      if (!editingMessage && !replyingTo) {
        setText('');
      }
      
      // Возвращаем фокус в поле ввода после завершения всех операций
      focusInput();
      
    } catch (error) {
      console.error('Error handling submission:', error);
      message.error('An error occurred during submission');
    } finally {
      setUploadingFile(false);
    }
  };

  // Show image in full-size lightbox
  const showLightbox = (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setLightboxImage(imageUrl);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxImage(null);
  };

  // Custom file item rendering - removing preview but keeping file info and view button
  const customItemRender = (originNode: React.ReactNode, file: any) => {
    const isImage = file.type && file.type.startsWith('image/');
    let iconElement = <FileOutlined />;
    
    // Safely try to extract icon from original node
    if (!isImage && React.isValidElement(originNode)) {
      const props = originNode.props as any;
      if (props && props.children && Array.isArray(props.children) && props.children.length > 0) {
        iconElement = props.children[0];
      }
    }
    
    return (
      <div className="custom-attachment-item">
        <div className="attachment-info">
          <div className="attachment-icon">
            {isImage ? <FileImageOutlined /> : iconElement}
          </div>
          <div className="attachment-name">{file.name}</div>
        </div>
        {isImage && file.originFileObj && (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              showLightbox(file.originFileObj as File);
            }}
          >
            {t('fileAttachment.view')}
          </Button>
        )}
      </div>
    );
  };

  // Определяем placeholder для поля ввода
  const getPlaceholder = () => {
    if (editingMessage) {
      return t('chat.editMessage');
    } else if (replyingTo) {
      return t('chat.replyTo');
    } else {
      return t('chat.messageInput');
    }
  };

  // Create the attachments header for Sender (hide if editing)
  const senderHeader = !editingMessage ? (
    <Sender.Header
      title={t('fileAttachment.attachments')}
      styles={{
        content: {
          padding: '8px',
        },
      }}
      open={attachmentsOpen}
      onOpenChange={setAttachmentsOpen}
      forceRender
    >
      <Attachments
        ref={attachmentsRef}
        beforeUpload={handleFileBeforeUpload}
        items={attachmentItems}
        onChange={({ fileList }) => setAttachmentItems(fileList)}
        placeholder={(type) =>
          type === 'drop'
            ? {
                title: t('fileAttachment.dropFileHere'),
              }
            : {
                icon: <CloudUploadOutlined />,
                title: t('fileAttachment.uploadFiles'),
                description: t('fileAttachment.clickOrDragFiles'),
              }
        }
        showUploadList={{
          showPreviewIcon: false,
          showDownloadIcon: false,
          showRemoveIcon: true,
        }}
        getDropContainer={() => senderRef.current?.nativeElement}
        itemRender={customItemRender}
      />
    </Sender.Header>
  ) : undefined;

  // Custom actions to override default sender behavior
  const customActions = () => {
    return (
      <Button
        type="primary"
        icon={<SendOutlined />}
        onClick={handleSubmit}
        disabled={!canSubmit || sendingMessage || uploadingFile}
        loading={sendingMessage || uploadingFile}
      />
    );
  };

  // Attachment button with badge (hide if editing)
  const renderAttachmentButton = () => {
    if (editingMessage) return null;
    
    return (
      <Badge count={attachmentItems.length} size="small" offset={[5, -3]} dot={false} showZero={false}>
        <Button
          type="text"
          icon={<LinkOutlined />}
          onClick={() => {
            setAttachmentsOpen(!attachmentsOpen);
          }}
        />
      </Badge>
    );
  };

  return (
    <>
      <Flex className="message-input-container" vertical>
        {/* Edit field */}
        {editingMessage && onCancelEdit && (
          <EditField 
            editingMessage={editingMessage} 
            onCancelEdit={onCancelEdit}
          />
        )}
        
        {/* Reply field */}
        {replyingTo && onCancelReply && (
          <ReplyField 
            replyToMessage={replyingTo} 
            onCancel={onCancelReply}
          />
        )}
        
        <Flex align="end">
          <Sender
            ref={senderRef}
            header={senderHeader}
            prefix={renderAttachmentButton()}
            value={text}
            onChange={handleTextChange}
            onPasteFile={(_, files) => {
              if (!editingMessage) {
                // Convert FileList to array to avoid iteration issue
                Array.from(files).forEach(file => {
                  attachmentsRef.current?.upload(file);
                });
                setAttachmentsOpen(true);
              }
            }}
            onSubmit={handleSubmit}
            submitType="enter"
            loading={sendingMessage || uploadingFile}
            disabled={sendingMessage || uploadingFile}
            placeholder={getPlaceholder()}
            actions={customActions}
          />
        </Flex>
      </Flex>

      {/* Image lightbox for full-size preview */}
      {lightboxImage && (
        <ImageLightbox src={lightboxImage} onClose={closeLightbox} />
      )}

      {/* Mentions autocomplete */}
      <MentionsAutocomplete
        show={mentionsVisible}
        position={mentionsPosition}
        query={mentionsQuery}
        onSelect={handleMentionSelect}
        onClose={hideMentions}
        currentRoomId={roomId}
      />
    </>
  );
};

export default MessageInput; 